<template>
  <v-app class="stock-app">
    <v-main>
      <div class="page">
        <header class="page-head">
          <h1 class="app-title">Motion</h1>
          <span v-if="lastSyncDate" class="sync-date">
            Остатки на {{ lastSyncDate }}
          </span>
        </header>

        <div class="search-panel">
          <FormFilter @updateForm="getStock" />
        </div>

        <div v-if="isLoading" class="cards">
          <v-skeleton-loader
            v-for="n in 6"
            :key="n"
            type="list-item-two-line"
            class="product-card"
          />
        </div>

        <template v-else>
          <div v-if="itemKeys.length" class="result-meta">
            Найдено: {{ itemKeys.length }}
          </div>

          <div class="cards">
            <v-card
              v-for="key in itemKeys"
              :key="key"
              class="product-card"
              flat
            >
              <div class="card-top">
                <h2 class="product-title">{{ items[key].product_title }}</h2>
                <span class="price">{{ formatPrice(items[key].price) }}</span>
              </div>
              <div class="size-chips">
                <span
                  v-for="(c, i) in items[key].characteristic"
                  :key="i"
                  class="size-chip"
                >
                  <template v-if="c.characteristic">
                    <b>{{ c.characteristic }}</b>
                    <span class="qty">· {{ c.quantity }}</span>
                  </template>
                  <template v-else>
                    <b>{{ c.quantity }} шт</b>
                  </template>
                </span>
              </div>
            </v-card>
          </div>

          <div v-if="!itemKeys.length" class="empty-state">
            <v-icon size="56" color="#C6CBD9">mdi-package-variant</v-icon>
            <p class="empty-title">Ничего не найдено</p>
            <p class="empty-hint">
              Проверьте название или уберите фильтр по размеру
            </p>
          </div>
        </template>
      </div>
    </v-main>
  </v-app>
</template>

<script>
import FormFilter from './components/FilterForm.vue';

export default {
  name: 'MainPage',
  components: { FormFilter },
  async mounted() {
    this.getStock({});
  },
  data() {
    return {
      items: {},
      isLoading: false,
      lastSyncDate: null,
    };
  },
  computed: {
    itemKeys() {
      return Object.keys(this.items);
    },
  },
  methods: {
    formatPrice(price) {
      const n = Number(price);
      if (isNaN(n)) return price;
      return `${n.toLocaleString('ru-RU')} ₸`;
    },
    getStock(data) {
      this.isLoading = true;
      this.$axios
        .get('/warehouse-stock', { params: { ...data } })
        .then((res) => {
          const result = {};
          res.data.forEach((e) => {
            if (!result[e.product_key]) {
              result[e.product_key] = { ...e };
              result[e.product_key].characteristic = [
                { characteristic: e.characteristic, quantity: e.quantity },
              ];
            } else {
              result[e.product_key].characteristic.push({
                characteristic: e.characteristic,
                quantity: e.quantity,
              });
            }

            result[e.product_key].characteristic.sort((a, b) => {
              return (a.characteristic || '').localeCompare(
                b.characteristic || '',
                'ru',
                { numeric: true },
              );
            });
          });

          this.items = result;

          const timestamps = res.data
            .map((e) => e.updatedAt)
            .filter((t) => !!t);
          if (timestamps.length) {
            this.lastSyncDate = new Date(
              Math.max(...timestamps.map((t) => new Date(t).getTime())),
            ).toLocaleString('ru-RU', {
              day: 'numeric',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit',
            });
          }
        })
        .catch((err) => console.log(err))
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<style scoped>
.stock-app {
  font-family:
    'Avenir Next',
    Avenir,
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    Roboto,
    sans-serif;
}

.page {
  max-width: 640px;
  margin: 0 auto;
  padding: 16px 16px 48px;
  text-align: left;
  color: #1c2130;
}

.page-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding: 4px 4px 12px;
}

.app-title {
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.sync-date {
  font-size: 12px;
  color: #6e7686;
  white-space: nowrap;
}

/* Sticky search: stays reachable while scrolling long stock lists */
.search-panel {
  position: sticky;
  top: 0;
  z-index: 5;
  margin: 0 -16px;
  padding: 8px 16px 12px;
  background: rgba(244, 245, 247, 0.92);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.result-meta {
  font-size: 13px;
  color: #6e7686;
  padding: 12px 4px 8px;
}

.cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 4px;
}

.product-card {
  border-radius: 16px !important;
  padding: 14px 16px;
  box-shadow: 0 1px 6px rgba(28, 33, 48, 0.06) !important;
}

.card-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.product-title {
  font-size: 15px;
  font-weight: 600;
  line-height: 1.35;
  letter-spacing: -0.01em;
}

.price {
  font-size: 15px;
  font-weight: 700;
  color: #3f51b5;
  white-space: nowrap;
}

.size-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding-top: 10px;
}

.size-chip {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 13px;
  line-height: 1;
  padding: 6px 10px;
  border-radius: 999px;
  background: #eef1fb;
  color: #33417c;
}

.size-chip .qty {
  color: #6e7686;
}

.empty-state {
  text-align: center;
  padding: 56px 16px 0;
}

.empty-title {
  font-size: 17px;
  font-weight: 600;
  padding-top: 12px;
}

.empty-hint {
  font-size: 14px;
  color: #6e7686;
  padding-top: 4px;
}
</style>
